/* eslint-disable @typescript-eslint/no-explicit-any */
// Meta Pixel "Purchase" tracking for Paymob success redirects.
//
// Why this exists: the Pixel tracks the whole funnel (PageView -> InitiateCheckout)
// but the final Purchase event was never firing, so Meta couldn't optimise the
// Sales campaigns or report ROAS. This records Purchase the moment Paymob sends the
// user back to the cart with ?success=true.
//
// How the amount is resolved (most reliable first):
//   1. amount_cents that Paymob appends to the redirect URL (the actual charge).
//   2. the value we stash in localStorage right before sending the user to Paymob
//      (survives the new tab + a cleared cart).
//   3. 0 as a last resort — the conversion still counts, only the value is missing.

const PENDING_KEY = "nw_pending_purchase";

/** Call right before redirecting the user to the Paymob checkout. */
export function setPendingPurchase(value: number, currency: string): void {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ value, currency, ts: Date.now() })
    );
  } catch {
    /* localStorage unavailable — fall back to Paymob query params on success */
  }
}

/** Call on the success redirect (when ?success=true is present). Fires once per order. */
export function firePurchaseFromSuccess(searchParams: URLSearchParams): void {
  const w = window as any;

  // 1) Best source: the amount Paymob appends to the redirect URL.
  const amountCents = searchParams.get("amount_cents");
  let value = amountCents ? Number(amountCents) / 100 : NaN;
  let currency = searchParams.get("currency") || "";

  // 2) Fallback: the amount we stored just before sending the user to Paymob.
  if (!value || Number.isNaN(value)) {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (raw) {
        const pending = JSON.parse(raw);
        value = Number(pending.value) || 0;
        currency = currency || pending.currency;
      }
    } catch {
      /* ignore malformed/absent pending value */
    }
  }

  if (!currency) currency = "EGP";
  if (!value || Number.isNaN(value)) value = 0;

  const orderId =
    searchParams.get("order") ||
    searchParams.get("id") ||
    searchParams.get("merchant_order_id") ||
    "";

  // De-dupe: refreshing the success URL shouldn't double-count the purchase.
  const dedupeKey = `nw_purchased_${orderId}`;
  try {
    if (orderId && sessionStorage.getItem(dedupeKey)) return;
    if (orderId) sessionStorage.setItem(dedupeKey, "1");
  } catch {
    /* ignore */
  }

  // eventID lets a future server-side (Conversions API) event de-duplicate with this one.
  const eventId = orderId ? `paymob_${orderId}` : undefined;

  if (typeof w.fbq === "function") {
    w.fbq(
      "track",
      "Purchase",
      { value, currency },
      eventId ? { eventID: eventId } : undefined
    );
  }

  // Also expose to GTM / GA4 for anyone wiring tags there.
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "purchase_success",
    value,
    currency,
    order_id: orderId,
    event_id: eventId,
  });

  // Clear the stash now that the purchase is recorded.
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
